import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { BaseParamsBuilderResourceQuery } from '~src/httpClient/resources/ResourceQuery';
import WebHttpClient from '~src/httpClient/WebHttpClient';

import { ContentRelayPlanMapper } from '../mappers/ContentRelayPlanMapper';
import { IContentRelayPlanInfo } from '../models/IContentRelayPlanInfo';
import {
  GetContentRelayPlansRequest,
  IGetContentRelayPlansParams,
  IGetContentRelayPlansResult,
} from '../serverApi/requests/GetContentRelayPlansRequest';

/**
 * Resource query backing the Content Relay plan list. Unlike standard VizPortal getItems APIs
 * (offset-paged), `listPlans` is token-paged and reads `state` / `pageSize` / `pageToken` payload
 * params, so we build those explicitly rather than reusing the generic ResourceQuery/fetcher.
 *
 * NOTE: `getSlice(start, size)` requests the first page only — token continuation
 * (`nextPageToken`) is not yet threaded through the offset-based slice contract. This is enough
 * for the initial list view; paging beyond the first page is a follow-up. See W-23846068.
 */
export class ContentRelayPlansResourceQuery extends BaseParamsBuilderResourceQuery<IContentRelayPlanInfo> {

  public getSlice(start: number, size: number): Promise<ListResult<IContentRelayPlanInfo>> {
    const extraParams = this.getExtraParams() || {};
    const params: IGetContentRelayPlansParams = {
      state: extraParams.state,
      pageSize: size != null ? size : undefined,
      pageToken: extraParams.pageToken,
    };

    return WebHttpClient.sendRequest(new GetContentRelayPlansRequest(params))
      .then((result: IGetContentRelayPlansResult) => {
        // The monolith Web API can return an HTTP 200 with an `errors` payload (e.g. code 17
        // UnexpectedErrorOccurred) when the downstream content-mobility gRPC service is not
        // reachable from the current dataplane. That resolves the promise rather than rejecting
        // it, so we detect it here and fall through to an empty list. Degrading to empty keeps
        // the Explorer on its configured empty state instead of rendering a blank/broken view.
        if (!result || (result.errors && result.errors.length > 0) || !result.plans) {
          return ListResult.empty<IContentRelayPlanInfo>();
        }
        const items = ContentRelayPlanMapper.map(result.plans);
        return new ListResult<IContentRelayPlanInfo>(items, start || 0, result.totalCount, [], !!result.nextPageToken);
      })
      .catch(() => {
        // Network/HTTP-level failure (rejected promise, e.g. the service endpoint is missing).
        // Degrade to an empty list rather than letting the rejection propagate to the space saga,
        // which would fail the whole fetch and blank the view.
        return ListResult.empty<IContentRelayPlanInfo>();
      });
  }

  public getCount(): Promise<number> {
    return this.getSlice(0, 1).then((slice: ListResult<IContentRelayPlanInfo>) => slice.total);
  }

  public getFirst(): Promise<IContentRelayPlanInfo> {
    throw new Error('ContentRelayPlansResourceQuery getFirst not implemented');
  }
}
